<script setup>
  import {inject, ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {useForm} from "@inertiajs/vue3";
  import {recordOriginKeys} from "@pages/Records/Enums/Enums.js";
  import {MedButton} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ComponentMergeRecordDialog",
  });

  const privacy = inject("$privacy");

  const refDialog = ref(null);
  const cannotMergeMultipleWaWiRecords = ref(false);
  const currentPatient = ref(null);

  const form = useForm({
    recordIds: [],
    info: null,
  });

  const open = async (patient, records) => {
    // check if there are more than 1 record
    // with wawi origin (pos.FullCheck)
    cannotMergeMultipleWaWiRecords.value =
      records.filter((record) => {
        return record.origin === recordOriginKeys.posFullcheck;
      }).length > 1;

    currentPatient.value = patient;
    form.recordIds = records.map((record) => record.id);

    // decrypt all record.info values in parallel
    const decryptedValues = await Promise.all(records.map((record) => privacy.decryptValue(record.info)));
    // concatenate all decrypted values with a line break and trim whitespace
    const resultString = decryptedValues.join("\n\n").trim();
    // encrypt concatenated value and assign it to form
    form.info = await privacy.encryptValue(resultString);

    refDialog.value.open();
  };

  const close = () => {
    // reset
    currentPatient.value = null;
    form.recordIds = [];

    refDialog.value?.close();
  };

  const save = () => {
    form.post(route("init-by-merging", {patient: currentPatient.value.id}), {
      preserveState: false,
      preserveScroll: false,
      onSuccess: () => {
        close();
      },
      onError: (error) => {
        console.error(error);
      },
    });
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refDialog"
    dialog-title="Analysen zusammenführen"
    dialog-width="small"
    :test-id="getTestId('merge-records-dialog')"
  >
    <template #content>
      <template v-if="cannotMergeMultipleWaWiRecords">
        <p>
          Sie haben
          <strong class="font-semibold">mehr als eine Analyse aus der Warenwirtschaft</strong>
          ausgewählt.
        </p>
        <p class="mt-2">
          Beim Zusammenführen von Analysen darf nur eine Analyse mit der Herkunft "WaWi" gekennzeichnet sein, damit das
          Ergebnis der Analyse wieder zurück an die Warenwirtschaft geschickt werden kann.
        </p>
      </template>
      <template v-else>
        <p>
          Die Eingabedaten der
          <strong class="font-semibold">{{ form.recordIds.length }}</strong>
          ausgewählten Analysen werden in eine neue Analyse zusammengeführt.
        </p>
        <p class="mt-2">Ihre bestehenden Analysen bleiben unverändert.</p>
      </template>
    </template>

    <template #actions>
      <template v-if="!cannotMergeMultipleWaWiRecords">
        <MedButton
          variant="primary"
          :disabled="form.processing"
          :data-test="getTestId('merge-records-dialog-save-button')"
          @click="save"
        >
          Zusammenführen
        </MedButton>
      </template>

      <MedButton :data-test="getTestId('merge-records-dialog-close-button')" @click="close">Abbrechen</MedButton>
    </template>
  </ComponentDialog>
</template>
